import { type WindowIdentityProps, type WindowKind, useDesktop } from "@/overlay/components";
import { Window, WindowSection, closeAction } from "@/overlay/components/window";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

import { AdvancedThresholdRow } from "@/overlay/components/CommonParts/Threshold/AdvancedThreshold/AdvancedThresholdRow";
import { type AdvancedThreshold, getDefaultAdvancedThreshold } from "@/overlay/components/CommonParts/Threshold/types";
import { ButtonBar } from "@/overlay/components/CommonParts/buttons";
import { DeleteConfirmationModal } from "@/overlay/components/window/DeleteConfirmationModal";
import { MmButton } from "@mm/zeus-react";
import React, { Fragment, useState } from "react";
import styles from "./ThresholdManagerWindow.module.scss";

export type ThresholdManagerWindowProps = {
  advancedThresholds: AdvancedThreshold[];
  onUpdate: (newThresholds: AdvancedThreshold[]) => void;
  isPoi?: boolean;
};

export default function ThresholdManagerWindow(props: WindowIdentityProps<WindowKind.ThresholdManagerWindow>) {
  const desktop = useDesktop();
  const { onUpdate, advancedThresholds, isPoi } = props.customProps;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const closeWindow = () => {
    desktop.dispatch({ type: "setWindowVisibility", windowId: props.instanceId, isVisible: false });
  };
  const actions = [closeAction(closeWindow)];

  const [thresholds, setThresholds] = useState<AdvancedThreshold[]>(
    advancedThresholds && advancedThresholds?.length > 0 ? advancedThresholds : [getDefaultAdvancedThreshold(0)],
  );
  /**
   * Handles updating a threshold.
   * @param partialThreshold The partial threshold containing updates.
   */
  const handleThresholdChange = (partialThreshold: Partial<AdvancedThreshold>) => {
    setThresholds((prevThresholds) =>
      prevThresholds.map((threshold) => {
        if (threshold.id === partialThreshold.id) {
          return { ...threshold, ...partialThreshold };
        }
        return threshold;
      }),
    );
  };

  const handleThresholdDelete = (id: string) => {
    setThresholds(thresholds.filter((threshold) => threshold.id !== id));
  };
  const handleDeleteAll = () => {
    setThresholds([]);
    onUpdate?.([]);
    closeWindow();
  };

  const addNewThreshold = () => {
    setThresholds([...thresholds, getDefaultAdvancedThreshold(thresholds.length + 1)]);
  };
  return (
    <Window kind="threshold-manager" title={t`Threshold manager`} {...props} actions={actions}>
      <WindowSection>
        <div>
          <Trans>advancedThresholdDescription</Trans>
        </div>
        <div className={styles["window-container"]}>
          {thresholds?.map((threshold) => {
            return (
              <Fragment key={threshold.id}>
                <AdvancedThresholdRow
                  threshold={threshold}
                  onChange={(updatedThreshold) => handleThresholdChange(updatedThreshold)}
                  onDelete={() => handleThresholdDelete(threshold.id)}
                  isPoi={isPoi}
                />
              </Fragment>
            );
          })}
          {thresholds.length < 3 && (
            <ButtonBar align={"center"} style={{ padding: "1rem" }}>
              <MmButton lefticonname={"add"} type={"secondary"} onClick={addNewThreshold}>
                <Trans>Add Threshold</Trans>
              </MmButton>
            </ButtonBar>
          )}
        </div>

        <ButtonBar align={"center"} hasSeparator>
          <MmButton type={"secondary"} lefticonname={"delete"} onClick={() => setIsDeleteModalOpen(true)}>
            <Trans>Remove All</Trans>
          </MmButton>
          <MmButton type={"secondary"} onClick={closeWindow}>
            <Trans>Cancel</Trans>
          </MmButton>
          <MmButton
            type="primary"
            onClick={() => {
              onUpdate?.(thresholds);
              closeWindow();
            }}
            aria-label="Submit"
          >
            Apply
          </MmButton>
        </ButtonBar>
      </WindowSection>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => {
          setIsDeleteModalOpen(false);
          handleDeleteAll();
        }}
      />
    </Window>
  );
}
